import { SidePanelRouter } from "@/app/router/SidePanelRouter";
import { SplitView } from "@/components/layout";
import { Sidebar } from "@/components/layout/components/sidebar/Sidebar";
import { TopBar } from "@/components/layout/components/top-bar";
import { mdsMediaQueries } from "@/design-system/foundations";
import { isMac } from "@/domains/platform/isMac";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { clientEnvModule } from "@/modules/client-env";
import { EventContext } from "@/domains/metrics/context";
import { useAuthenticatedMetrics } from "@/app/outlets/layout/hooks";
import { StandardModeAppAppOverlays } from "@/app/outlets/layout/standard-mode-app-overlays";
import { ImportWizard } from "@/components/layout/components/sidebar/import-wizard";
import { SystemAudioBridgeWrapper } from "@/components/desktop/system-audio";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { CenterPanelSplitView } from "@/components/layout/components/center-panel-split-view/CenterPanelSplitView";
import { noop } from "lodash-es";

interface StandardModeAppLayoutOutletProps {}

interface KeyboardShortcut {
  condition: ({ event }: { event: KeyboardEvent }) => boolean;
  key: string;
  action: ({ event }: { event: KeyboardEvent }) => void;
}

export const StandardModeAppLayoutOutlet = observer<StandardModeAppLayoutOutletProps>(() => {
  useAuthenticatedMetrics();
  const { store, pageStore } = useAppStore();

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const handleCloseImportModal = useCallback(() => {
    setIsImportModalOpen(false);
  }, []);

  const isDesktop = clientEnvModule.isDesktop();

  const keyboardShortcuts: KeyboardShortcut[] = useMemo(
    () => [
      {
        condition: ({ event }) =>
          store.platformInfo.onboardingInfo.onboardingIsCompleted &&
          !pageStore.quickSearchModal.isOpen &&
          !store.notes.newNoteIsBeingCreated &&
          (isMac() ? (isDesktop ? event.metaKey : event.ctrlKey) : event.altKey),
        key: "n",
        action: () => store.notes.composeNewNote({ eventContext: EventContext.KeyboardShortcut }),
      },
      {
        condition: ({ event }) =>
          store.platformInfo.onboardingInfo.onboardingIsCompleted &&
          (isMac() ? event.metaKey : event.ctrlKey),
        key: "k",
        action: pageStore.quickSearchModal.toggleIsOpen,
      },
      {
        condition: ({ event }) =>
          store.platformInfo.onboardingInfo.onboardingIsCompleted &&
          !pageStore.quickSearchModal.isOpen &&
          (isMac() ? event.metaKey : event.ctrlKey),
        key: "j",
        action: () =>
          store.sidePanel.goToMemIt(undefined, {
            openChatPanel: true,
          }),
      },
      {
        condition: ({ event }) =>
          store.platformInfo.onboardingInfo.onboardingIsCompleted &&
          !event.shiftKey &&
          !event.altKey &&
          !event.ctrlKey &&
          !event.metaKey,
        key: "Escape",
        action: () => store.navigation.tabs.openInNewTab({ path: "/notes" }),
      },
      {
        condition: ({ event }) =>
          store.platformInfo.onboardingInfo.onboardingIsCompleted &&
          !pageStore.quickSearchModal.isOpen &&
          store.debug.debugModeEnabled &&
          isDesktop &&
          isMac() &&
          event.metaKey,
        key: "f",
        action: () => store.interface.setIsFindInPageOpen(true),
      },
      {
        condition: ({ event }) =>
          store.platformInfo.onboardingInfo.onboardingIsCompleted &&
          (isMac() ? (isDesktop ? event.metaKey : event.ctrlKey) : event.altKey),
        key: "w",
        action: () => {
          const activeTab = store.navigation.tabs.tabsMap.get(store.navigation.tabs.activeTabId);
          if (!activeTab?.isDefault) {
            store.navigation.tabs.closeTab({ tabId: store.navigation.tabs.activeTabId });
          }
        },
      },
      {
        condition: ({ event }) =>
          store.platformInfo.onboardingInfo.onboardingIsCompleted &&
          (isMac() ? (isDesktop ? event.metaKey : event.ctrlKey) : event.altKey),
        key: "t",
        action: pageStore.quickSearchModal.toggleIsOpen,
      },
    ],
    [pageStore, store, isDesktop]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      for (const shortcut of keyboardShortcuts) {
        if (event.key === shortcut.key && shortcut.condition({ event })) {
          event.preventDefault();
          event.stopPropagation();

          shortcut.action({ event });

          return;
        }
      }
    },
    [keyboardShortcuts]
  );

  useLayoutEffect(() => {
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  const currentSidebarVariant = store.layout.currentSidebarVariant;
  const shouldHideSidebarToggle = store.layout.shouldHideSidebarToggle;
  const toggleSidebar = store.interface.toggleSidebar;

  useEffectOnMount(() => {
    // The sidebar state starts as closed. Determine if it should be opened on load.
    if (store.interface.isSidebarOpen) return;
    if (store.layout.hasRoomForSidebarOnInitialLoad) {
      store.interface.setIsSidebarOpen(true, { animate: false });
    }
  });

  return (
    <>
      <ImportWizard isOpen={isImportModalOpen} onClose={handleCloseImportModal} onSkip={noop} />
      <StandardModeAppAppOverlays />
      <SystemAudioBridgeWrapper />
      <OuterWrapper data-component="OuterWrapper">
        <Sidebar
          currentSidebarVariant={currentSidebarVariant}
          setIsImportModalOpen={setIsImportModalOpen}
          shouldHideSidebarToggle={shouldHideSidebarToggle}
          toggleSidebar={toggleSidebar}
          data-component="Sidebar"
        />
        <InnerWrapper data-component="InnerWrapper">
          <TopBar
            setIsImportModalOpen={setIsImportModalOpen}
            currentSidebarVariant={currentSidebarVariant}
            data-component="TopBar"
          />
          <Main data-component="Main">
            <SplitView sidePanel={<SidePanelRouter />}>
              <CenterPanel data-component="CenterPanel">
                <CenterPanelSplitView data-component="CenterPanelSplitView" />
              </CenterPanel>
            </SplitView>
          </Main>
        </InnerWrapper>
      </OuterWrapper>
    </>
  );
});

const OuterWrapper = styled.div`
  height: 100%;
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  background-color: ${({ theme }) => theme.colors.surface.chrome};
  min-height: 0;
`;
OuterWrapper.displayName = "OuterWrapper";

const InnerWrapper = styled.div({
  height: "100%",
  width: "100%",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  flexShrink: 1,
});
InnerWrapper.displayName = "InnerWrapper";

const Main = styled("div")({
  height: "100%",
  width: "100%",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  flexShrink: 1,
  minHeight: 0,
  [mdsMediaQueries().rules.tablet]: {
    paddingLeft: 0,
  },
  [mdsMediaQueries().rules.desktop]: {
    paddingLeft: 0,
  },
});
Main.displayName = "Main";

const CenterPanel = styled.div({
  width: "100%",
  height: "100%",
});
CenterPanel.displayName = "CenterPanel";
